import {
  DEFAULT_MAX_REQUEST_BODY_BYTES,
  jsonUtf8ByteLength,
  type createApiClient,
  type ProfileVisibility,
  type PublicIdentity,
} from '@adieuu/shared';
import {
  clearBytes,
  deriveEntropyWrappingKey,
  getSigningPublicKey,
  toBase64,
} from '@adieuu/crypto';
import {
  generateE2EKeys,
  getDefaultDeviceName,
} from './e2eKeyService';
import { getOrCreateWrappingSalt, storeDeviceKeys } from './deviceKeyStorage';
import { generateAndUploadPreKeys } from './preKeyService';
import { buildDeviceStaticKeyAttestationB64 } from './deviceStaticKeyAttestationUpload';
import type { CreateIdentityResult } from '../hooks/useIdentity.types';

type ApiCreateErrorCode = NonNullable<CreateIdentityResult['errorCode']>;

const CREATE_API_ERROR_CODES = new Set<string>([
  'USERNAME_TAKEN',
  'MAX_ALIASES',
  'PASSPHRASE_EXISTS',
  'USERNAME_BLOCKED',
  'USERNAME_NOT_GENERATED',
  'DISPLAY_NAME_BLOCKED',
  'VALIDATION_ERROR',
  'NO_SESSION',
  'E2E_INIT_FAILED',
  'PAYLOAD_TOO_LARGE',
  'GEOFENCE_BLOCKED',
  'AGE_VERIFICATION_REQUIRED',
  'AGE_VERIFICATION_FAILED',
  'AGE_VERIFICATION_COOLDOWN',
]);

/** Maps `error.code` from POST /identity to the client create result. */
export function resolveCreateIdentityApiError(
  code: string | undefined,
  fallbackMessage: string,
): CreateIdentityResult {
  const errorCode: ApiCreateErrorCode = CREATE_API_ERROR_CODES.has(code ?? '')
    ? (code as ApiCreateErrorCode)
    : 'VALIDATION_ERROR';
  return { success: false, error: fallbackMessage, errorCode };
}

type ApiClient = ReturnType<typeof createApiClient>;

export interface CreateIdentityFlowSuccess {
  ok: true;
  identity: PublicIdentity;
  wrappingKey: Uint8Array;
  wrappingSalt: Uint8Array;
  signingPrivateKey: Uint8Array;
  pqSigningPrivateKey?: Uint8Array;
  deviceId: string;
}

export interface CreateIdentityFlowFailure {
  ok: false;
  result: CreateIdentityResult;
}

export type CreateIdentityFlowResult =
  | CreateIdentityFlowSuccess
  | CreateIdentityFlowFailure;

export async function runCreateIdentityFlow(
  api: ApiClient,
  platform: string,
  signedToken: string,
  passphrase: string,
  username: string,
  displayName: string,
  displayNamePrivacy?: ProfileVisibility
): Promise<CreateIdentityFlowResult> {
  const response = await api.identity.create({
    signedToken,
    passphrase,
    username,
    displayName,
    displayNamePrivacy,
  });
  if (!response.success) {
    const errorMessage = response.error?.message ?? 'Failed to create alias';
    return {
      ok: false,
      result: resolveCreateIdentityApiError(response.error?.code, errorMessage),
    };
  }

  const responseData = response.data as PublicIdentity | undefined;
  if (!responseData) {
    return {
      ok: false,
      result: {
        success: false,
        error: 'Alias creation returned no data',
        errorCode: 'VALIDATION_ERROR',
      },
    };
  }

  const createdIdentity = responseData;

  let e2eResult: Awaited<ReturnType<typeof generateE2EKeys>>;
  try {
    e2eResult = await generateE2EKeys({
      identityId: createdIdentity.id,
      passphrase,
      deviceName: getDefaultDeviceName(),
      cryptoProfile: 'default',
    });
    console.log(
      '[Identity] createIdentity: SIGNING KEY DEBUG - public key to upload:',
      e2eResult.signingPublicKey
    );
    console.log(
      '[Identity] createIdentity: SIGNING KEY DEBUG - derived from private:',
      toBase64(getSigningPublicKey(e2eResult.signingPrivateKey))
    );
  } catch {
    return {
      ok: false,
      result: {
        success: false,
        error: 'Failed to generate encryption keys',
        errorCode: 'E2E_INIT_FAILED',
      },
    };
  }

  clearBytes(e2eResult.webDevice.privateKeys.ecdh);
  clearBytes(e2eResult.webDevice.privateKeys.kem);

  try {
    const sessionCheck = await api.identity.getSession();
    if (!sessionCheck.success || sessionCheck.data?.id !== createdIdentity.id) {
      clearBytes(e2eResult.signingPrivateKey);
      if (e2eResult.pqSigningPrivateKey) clearBytes(e2eResult.pqSigningPrivateKey);
      clearBytes(e2eResult.devicePrivateKeys.ecdh);
      clearBytes(e2eResult.devicePrivateKeys.kem);
      return {
        ok: false,
        result: {
          success: false,
          error: 'Alias session not established. Please try again.',
          errorCode: 'E2E_INIT_FAILED',
        },
      };
    }
  } catch {
    clearBytes(e2eResult.signingPrivateKey);
    if (e2eResult.pqSigningPrivateKey) clearBytes(e2eResult.pqSigningPrivateKey);
    clearBytes(e2eResult.devicePrivateKeys.ecdh);
    clearBytes(e2eResult.devicePrivateKeys.kem);
    return {
      ok: false,
      result: {
        success: false,
        error: 'Failed to verify alias session',
        errorCode: 'E2E_INIT_FAILED',
      },
    };
  }

  try {
    if (!e2eResult.pqSigningPublicKey || !e2eResult.pqSigningPrivateKey) {
      return {
        ok: false,
        result: {
          success: false,
          error: 'Failed to generate encryption keys',
          errorCode: 'E2E_INIT_FAILED',
        },
      };
    }

    const staticKeyAttestation = buildDeviceStaticKeyAttestationB64({
      signingPrivateKey: e2eResult.signingPrivateKey,
      profile: 'default',
      deviceId: e2eResult.device.deviceId,
      ecdhPublicKey: e2eResult.device.ecdhPublicKey,
      kemPublicKey: e2eResult.device.kemPublicKey,
      pqSigningPrivateKey: e2eResult.pqSigningPrivateKey,
    });
    const initBody = {
      signingPublicKey: e2eResult.signingPublicKey,
      pqSigningPublicKey: e2eResult.pqSigningPublicKey,
      preferredCryptoProfile: 'default' as const,
      device: { ...e2eResult.device, staticKeyAttestation },
      bundle: e2eResult.encryptedBundle,
    };
    const initBytes = jsonUtf8ByteLength(initBody);
    if (initBytes > DEFAULT_MAX_REQUEST_BODY_BYTES) {
      clearBytes(e2eResult.signingPrivateKey);
      if (e2eResult.pqSigningPrivateKey) clearBytes(e2eResult.pqSigningPrivateKey);
      clearBytes(e2eResult.devicePrivateKeys.ecdh);
      clearBytes(e2eResult.devicePrivateKeys.kem);
      return {
        ok: false,
        result: {
          success: false,
          error: `Encryption setup payload is too large (${(initBytes / 1024).toFixed(1)} KiB; max ${(DEFAULT_MAX_REQUEST_BODY_BYTES / 1024).toFixed(0)} KiB).`,
          errorCode: 'PAYLOAD_TOO_LARGE',
        },
      };
    }
    const initResponse = await api.identity.initializeE2E(createdIdentity.id, initBody);
    if (!initResponse.success) {
      console.error(
        '[Identity] createIdentity: E2E initialize failed:',
        initResponse.error?.message,
      );
      clearBytes(e2eResult.signingPrivateKey);
      if (e2eResult.pqSigningPrivateKey) clearBytes(e2eResult.pqSigningPrivateKey);
      clearBytes(e2eResult.devicePrivateKeys.ecdh);
      clearBytes(e2eResult.devicePrivateKeys.kem);
      return {
        ok: false,
        result: {
          success: false,
          error: 'Failed to upload encryption keys to server',
          errorCode: 'E2E_INIT_FAILED',
        },
      };
    }
  } catch {
    clearBytes(e2eResult.signingPrivateKey);
    if (e2eResult.pqSigningPrivateKey) clearBytes(e2eResult.pqSigningPrivateKey);
    clearBytes(e2eResult.devicePrivateKeys.ecdh);
    clearBytes(e2eResult.devicePrivateKeys.kem);
    return {
      ok: false,
      result: {
        success: false,
        error: 'Failed to initialize encryption',
        errorCode: 'E2E_INIT_FAILED',
      },
    };
  }

  let wrappingKey: Uint8Array;
  let wrappingSalt: Uint8Array;
  try {
    wrappingSalt = await getOrCreateWrappingSalt(createdIdentity.id);
    wrappingKey = await deriveEntropyWrappingKey(passphrase, wrappingSalt);
  } catch {
    clearBytes(e2eResult.signingPrivateKey);
    if (e2eResult.pqSigningPrivateKey) clearBytes(e2eResult.pqSigningPrivateKey);
    clearBytes(e2eResult.devicePrivateKeys.ecdh);
    clearBytes(e2eResult.devicePrivateKeys.kem);
    return {
      ok: false,
      result: {
        success: false,
        error: 'Failed to derive wrapping key',
        errorCode: 'E2E_INIT_FAILED',
      },
    };
  }

  try {
    await storeDeviceKeys(
      e2eResult.device.deviceId,
      createdIdentity.id,
      e2eResult.devicePrivateKeys.ecdh,
      e2eResult.devicePrivateKeys.kem,
      wrappingKey,
      e2eResult.device.routingTag
    );
  } catch {
    clearBytes(wrappingKey);
    clearBytes(e2eResult.signingPrivateKey);
    if (e2eResult.pqSigningPrivateKey) clearBytes(e2eResult.pqSigningPrivateKey);
    return {
      ok: false,
      result: {
        success: false,
        error: 'Failed to store device keys',
        errorCode: 'E2E_INIT_FAILED',
      },
    };
  }

  try {
    await generateAndUploadPreKeys(
      {
        identityId: createdIdentity.id,
        deviceId: e2eResult.device.deviceId,
        signingPrivateKey: e2eResult.signingPrivateKey,
        wrappingKey,
        platform: platform as never,
        pqSigningPrivateKey: e2eResult.pqSigningPrivateKey,
      },
      api.identity
    );
  } catch {
    // Non-fatal.
  }

  return {
    ok: true,
    identity: createdIdentity as PublicIdentity,
    wrappingKey,
    wrappingSalt,
    signingPrivateKey: e2eResult.signingPrivateKey,
    pqSigningPrivateKey: e2eResult.pqSigningPrivateKey,
    deviceId: e2eResult.device.deviceId,
  };
}
